import { useEffect, useRef } from "react";
import {
    useCurrentRapidData,
    useInitializeModelFromRapidData,
    useFetchCurrentModelMetrics,
    useFetchComparisonSources,
    useCurrentModelChange,
    useModelPageState
} from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useUser } from "@/contexts/UserContext";
import { ModelPageState } from "../types";

export function useModelPage() {
    const rapidData = useCurrentRapidData();
    const user = useUser();
    const { currentWorkspace, apiKey } = useCurrentWorkspace();
    const initializeModel = useInitializeModelFromRapidData();
    const fetchMetrics = useFetchCurrentModelMetrics();
    const fetchComparisonSources = useFetchComparisonSources();
    const currentModelChange = useCurrentModelChange();
    const modelPageState = useModelPageState();

    // Initialize model state from rapid data once per mount. After the first
    // successful init, block re-initialization so that Firestore updates
    // (e.g., after marking as seen) don't wipe the current view mid-session.
    // If model state is cleared (e.g., by unmount cleanup in strict mode),
    // allow re-initialization by resetting the guard.
    const isInitializedRef = useRef(false);

    useEffect(() => {
        if (modelPageState === ModelPageState.LOADING) {
            isInitializedRef.current = false;
        }
        if (rapidData && user?.uid && !isInitializedRef.current) {
            initializeModel(rapidData, user.uid);
            isInitializedRef.current = true;
        }
    }, [rapidData, user?.uid, initializeModel, modelPageState]);

    // Fetch metrics when current model change is set and has a version ref
    useEffect(() => {
        if (currentModelChange?.to?.ref) {
            fetchMetrics();
        }
    }, [currentModelChange?.to?.ref, fetchMetrics]);

    // Fetch comparison sources when we're in comparison or single model mode and have the model data
    useEffect(() => {
        if (
            rapidData?.id &&
            apiKey &&
            currentModelChange &&
            (modelPageState === ModelPageState.NEW_MODEL_COMPARISON ||
                modelPageState === ModelPageState.HISTORICAL_COMPARISON ||
                modelPageState === ModelPageState.SINGLE_MODEL_PERFORMANCE)
        ) {
            fetchComparisonSources(rapidData.id, apiKey);
        }
    }, [rapidData?.id, apiKey, currentModelChange?.id, modelPageState, fetchComparisonSources]);
}
